<script lang="ts">
    import cn from 'classnames';
    import { onMount } from 'svelte';
    import { classList } from 'svelte-body';
    import { captureMetrics } from '$lib/metrics';
    import { configService, gtmService } from '$lib/services';
    import { sendLoginEvent } from '$lib/services/gtm/methods';
    import { recaptchaService } from '$lib/services/recaptcha';

    import { AllowCookies } from '@components/layout/allow-cookies';
    import { Dialogs } from '@components/layout/dialogs';
    import { Footer } from '@components/layout/footer';
    import { Header } from '@components/layout/header';
    import { HrefLangs } from '@components/layout/hreflangs';
    import { paymentStore } from '@components/payment/store';
    import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
    import { bodyClass, globalStore, isInternetAvailable } from '@components/stores';
    import { prevPagePath } from '@components/stores/global';
    import { pageVisibilityStore } from '@components/stores/page-visibility/page-visibility';
    import { dialogStore } from '@components/ui/dialog';
    import { Toasts, toastsStore } from '@components/ui/toasts';
    import { userStore } from '@components/user/store';
    import LL, { setLocale } from '@i18n/i18n-svelte';
    import { Cookie } from '@shared/cookies';
    import { changeRouteLocale, landingPages, URLS } from '@shared/routing';
    import {
        AuthMethod,
        EventType,
        Feature,
        LogType,
        Orientation,
    } from '@shared/types';
    import { getOrientation } from '@shared/utils/get-orientation';
    import { getCookie } from '@utils/cookies';
    import { isTouch } from '@utils/device';
    import { getRegSource, getUserType } from '@utils/stats';

    import type { LayoutData } from './$types';

    import '$lib/styles/design-tokens.css';
    import s from './layout.module.css';

    import { browser } from '$app/environment';
    import { beforeNavigate } from '$app/navigation';
    import { page } from '$app/stores';

    export let data: LayoutData;

    setLocale(data.locale);
    changeRouteLocale(data.locale);

    if (data.config.gtmId) {
        const { init } = gtmService();
        init(data.config.gtmId, data.gtmInitials);
    }

    globalStore.init(data.storeInitials);
    configService.init(data.config);
    recaptchaService.setConfig(
        configService.isFeatureAllowed(Feature.Recaptcha),
        configService.get('recaptchaParam')
    );

    $: data.user ? userStore.setUser(data.user) : userStore.setUser(undefined);
    $: data.userSubscription
        ? paymentStore.setCurrentSubscription(data.userSubscription)
        : paymentStore.setCurrentSubscription(null);
    $: data.userTimePackage
        ? paymentStore.setCurrentTimePackage(data.userTimePackage)
        : paymentStore.setCurrentTimePackage(null);

    $: isLandingPage = landingPages().includes($page.url.pathname);
    $: isAuth = Boolean($userStore.user);

    const minDesktopWidth: number | null = browser ? parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--xh-min-desktop-width')
    ) : null;

    let appHeight = 0;
    const setAppHeightAndOrientation = () => {
        const orientation: Orientation = getOrientation(window.innerWidth, window.innerHeight);
        globalStore.setOrientation(orientation);

        if (appHeight !== window.innerHeight) {
            appHeight = window.innerHeight;
            document.documentElement.style.setProperty('--xh-app-height', `${appHeight}px`);
        }

        if (minDesktopWidth === null || !browser) {
            return;
        }

        if (window.innerWidth < minDesktopWidth && $globalStore.isDesktopSite) {
            globalStore.switchToDesktop(false, false);
        }

        if (
            window.innerWidth >= minDesktopWidth &&
            !$globalStore.isDesktopSite &&
            getCookie(Cookie.IsDesktopSite) !== 'false'
        ) {
            globalStore.switchToDesktop(true, false);
        }
    };

    beforeNavigate(({ type }) => {
        prevPagePath.set($page.url.pathname);
        if (type === 'popstate') {
            dialogStore.closeDialog();
        }
    });

    const checkLoginSource = () => {
        const loginMethod = $page.url.searchParams.get('successLogin');
        if (loginMethod) {
            sendLoginEvent({
                event: EventType.LoginSuccess,
                type: getUserType($userStore.user),
                reg_source: getRegSource(loginMethod as AuthMethod),
            });
        }
    };

    onMount(() => {
        // some tablets defined as a desktop, but it is necessary to set the mobile view
        if (!getCookie(Cookie.IsDesktopSite) && $globalStore.isDesktopSite && isTouch()) {
            globalStore.switchToDesktop(false);
        }
        checkLoginSource();
        setAppHeightAndOrientation();

        const version = getCookie(Cookie.Version);
        if (version) {
            window.onMount(version);
            captureMetrics(
                version,
                $globalStore.isDesktopSite,
                $page.url.pathname
            );
        }
    });

    function onOffline() {
        streamingLoggerStore.log({ type: LogType.App, message: 'Svelte: Layout: onOffline' });
        isInternetAvailable.set(false);
    }

    function onOnline() {
        streamingLoggerStore.log({ type: LogType.App, message: 'Svelte: Layout: onOnline' });
        isInternetAvailable.set(true);
    }

    function handleVisibilityChange() {
        pageVisibilityStore.setIsHidden(document.hidden);
    }
</script>

<svelte:window on:resize={setAppHeightAndOrientation} on:offline={onOffline} on:online={onOnline} />
<svelte:head>
    {#if isAuth}<meta name="RATING" content="RTA-5042-1996-1400-1577-RTA" />{/if}
    <HrefLangs />
</svelte:head>
<svelte:body use:classList={cn({ [s.desktop]: $globalStore.isDesktopSite }, $bodyClass)} />
<svelte:document on:visibilitychange={handleVisibilityChange} />

<div class={cn(s.app, { [s.desktop]: $globalStore.isDesktopSite })}>
    <div class={s.appInner}>
        {#if $globalStore.isShowHeader}
            <Header isDesktop={$globalStore.isDesktopSite}/>
        {/if}

        <div class={cn({ [s.mainWrapper]: !isLandingPage })}>
            <slot>{$LL.notFoundPage.title()}</slot>
        </div>
    </div>

    {#if $globalStore.isShowFooter}
        <Footer isOperator={Boolean($userStore.user?.operator)} />
    {/if}

    {#if !configService.get('isUnsupportedDevice')}
        {#if !$globalStore.isCookiesAllowed}
            <AllowCookies isAuth={Boolean($userStore.user)} />
        {/if}

        {#if $dialogStore.isOpened}
            <Dialogs />
        {/if}

        {#if browser && $toastsStore.toasts.size > 0}
            <Toasts />
        {/if}

        {#if
            $userStore.user
            && $page.url.pathname !== URLS.paymentFailure()
            && $page.url.pathname !== URLS.paymentSuccess()
            && $page.url.pathname !== URLS.paymentFailed()
            && $page.url.pathname !== URLS.sessionExists()
        }
            {#await import('@components/layout/public-auth') then { PublicAuth } }
                <PublicAuth/>
            {/await}
        {/if}
    {/if}
</div>
